import {Injectable} from '@angular/core';
import {
  RefundService as Api,
  CartService as CartApi,
  Cart,
  RefundCart,
  TicketIds,
  TotalInsurance,
  TotalSurcharge,
  Transaction,
  DeliveryType,
  RefundTicketRange,
  EventRight,
  RefundTickets,
  CartTotalSurcharge,
  SurchargeType,
  CartTotal,
  PurchaseTransaction,
  EventPaymentReversalsInfo,
  PaymentReversalPage,
  RefundStatusEnum,
  PaymentMethodEnum,
  CartEvent
} from '../../generated/sales-terminal-client';
import {LanguageService} from '../language/language.service';
import {StorageService} from '../storage/storage.service';
import {cloneDeep, sumBy, uniq} from 'lodash-es';
import {Observable, tap} from 'rxjs';
import {SnackService} from '../snack/snack.service';
import moment from 'moment';
import {UtilService} from '../util/util.service';
import {AuthService} from '../auth/auth.service';
import {RouteService} from '../route/route.service';
import {PageableSearch} from "../../components/scroll-load-component/scroll-load.component";

export interface PaymentReversalsFilter extends PageableSearch {
  reversalStartDate?: string;
  reversalEndDate?: string;
  refundStatus?: RefundStatusEnum;
  orderConfirmation?: number;
  eventId?: number;
}

@Injectable()
export class RefundService {
  static STORAGE_REFUND_UPDATE = 'refund.update';
  static STORAGE_REFUND_EXCHANGE = 'refund.exchange';
  static STORAGE_REFUND_EVENTS = 'refund.events';
  static STORAGE_REFUND_EVENTS_DESELECTED = 'refund.events.deselected';

  protected _emptyTotals: CartTotal = {
    surcharges: [],
    insurances: [],
    ticketTotal: 0,
    total: 0,
    ticketQuantity: 0,
    giftCardCreditTotal: 0
  };
  protected _emptyCart: Cart = {
    events: [],
    giftCards: [],
    deliveryConfiguration: null,
    legacyPromocode: null,
    discountPercent: 0,
    total: this._emptyTotals
  };
  private _cart: Cart = cloneDeep(this._emptyCart);
  public get cart() {
    return this._cart;
  }
  public set cart(value: Cart) {
    this._cart = value;
  }

  public giftTicketIds: number[] = [];
  public nonRefundableSurcharges: TotalSurcharge[] = [];
  public refundableInsurances: TotalInsurance[] = [];
  public nonRefundableInsurances: TotalInsurance[] = [];
  public isLoading: boolean;
  public clickableTicketIds: number[] = [];

  private _selectedTicketIds: number[] = [];
  public get selectedTicketIds(): number[] {
    return this._selectedTicketIds;
  };
  public set selectedTicketIds(value: number[]) {
    const diffIds: number[] = this._selectedTicketIds.filter(id => !value.includes(id))
      .concat(value.filter(id => !this._selectedTicketIds.includes(id)));
    const areTicketsRemoved: boolean = this._selectedTicketIds.length > value.length;
    this._selectedTicketIds = value;
    this.updateDeselectedTickets(diffIds, areTicketsRemoved);
  }

  private _refundEvents: CartEvent[] = [];
  public get refundEvents(): CartEvent[] {
    return this._refundEvents;
  };
  public set refundEvents(events: CartEvent[]) {
    this._refundEvents = events;
    this.storageService.set(RefundService.STORAGE_REFUND_EVENTS, JSON.stringify(this._refundEvents));
  }

  private _deSelectedEvents: CartEvent[] = [];
  public get deSelectedEvents(): CartEvent[] {
    return this._deSelectedEvents;
  }
  public set deSelectedEvents(events: CartEvent[]) {
    this._deSelectedEvents = uniq(events);
    this.storageService.set(RefundService.STORAGE_REFUND_EVENTS_DESELECTED, JSON.stringify(this._deSelectedEvents));
  }

  constructor(private api: Api,
              private cartApi: CartApi,
              private authService: AuthService,
              private languageService: LanguageService,
              private storageService: StorageService,
              private routeService: RouteService,
              private snackService: SnackService) {
  }

  public get ticketQuantity(): number {
    return this.cart?.total?.ticketQuantity || 0;
  }

  public get cartTotal(): number {
    return this.cart.total && (this.cart.total.ticketTotal + sumBy(this.cartHiddenFees, 'sum')) || 0;
  }

  public get cartTotalSum(): number {
    return this.cart.total?.total || 0;
  }

  public get cartDelivery(): DeliveryType {
    return this.cart.total?.legacyDelivery || null;
  }

  public get cartFees(): CartTotalSurcharge[] {
    return (this.cart.total?.surcharges || []).filter(fee => (fee.type !== SurchargeType.Hidden));
  }

  private get cartHiddenFees(): CartTotalSurcharge[] {
    return (this.cart.total?.surcharges || []).filter(fee => (fee.type === SurchargeType.Hidden));
  }

  public addTicketsRange(range: RefundTicketRange): Observable<RefundCart> {
    return this.api.refundRangePost(range).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public addTickets(ticketIds: TicketIds): Observable<RefundCart> {
    return this.api.refundCartAddPost(ticketIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public removeTickets(tickets: TicketIds): Observable<Cart> {
    return this.cartApi.removeTicketsFromCart(tickets).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public startRefund(tickets: RefundTickets): Observable<Transaction> {
    return this.api.refundPost(tickets).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public canRefund(eventId: number): Observable<EventRight> {
    return this.api.canRefund(eventId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public refundFiscal(transactionId: number): Observable<Blob> {
    return this.api.printFiscalRefundGet(transactionId, this.languageService.selectedLang).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public getCart(): Observable<RefundCart> {
    return this.api.refundGet().pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
  public updateCart(updateTickets: boolean = false, returnAfterUpdate: boolean = false): void {
    this.storageService.set(RefundService.STORAGE_REFUND_UPDATE, moment.now());
    this.isLoading = true;
    this.getCart().pipe(
      tap({error: () => this.isLoading = false})
    ).subscribe((refundCart: RefundCart) => {
      this.cart = refundCart.cart;
      this.refundEvents = JSON.parse(this.storageService.get(RefundService.STORAGE_REFUND_EVENTS)) || [];
      if (updateTickets || (!this.refundEvents.length && refundCart.cart?.total?.ticketQuantity)) {
        let updatedRefundEvents = refundCart.cart.events || [];
        this.clickableTicketIds = [];
        this._selectedTicketIds = [];
        updatedRefundEvents.forEach(event => {
          this._selectedTicketIds = this._selectedTicketIds.concat(event.legacyTickets.map(ticket => ticket.id));
        });
        this.deSelectedEvents = JSON.parse(this.storageService.get(RefundService.STORAGE_REFUND_EVENTS_DESELECTED)) || [];
        let deSelectedTicketIds: number[] = [];
        this.deSelectedEvents.forEach(deSelectedEvent => {
          const refundEvent: CartEvent = updatedRefundEvents.find(refundEvent => refundEvent.id === deSelectedEvent.id);
          if (!refundEvent) {
            updatedRefundEvents.push(deSelectedEvent);
          } else {
            refundEvent.legacyTickets = refundEvent.legacyTickets.concat(deSelectedEvent.legacyTickets);
          }
          deSelectedTicketIds = deSelectedTicketIds.concat(deSelectedEvent.legacyTickets.map(ticket => ticket.id));
        });
        this.clickableTicketIds = this._selectedTicketIds.concat(deSelectedTicketIds);
        this.refundEvents = updatedRefundEvents;
      }
      if (returnAfterUpdate && !this.clickableTicketIds?.length) {
        if (!this.authService.checkSalespointProperty('fiscal_procedures_lt') || !UtilService.getBrowser('ff')) {
          if (this.storageService.get(RefundService.STORAGE_REFUND_EXCHANGE)) {
            this.routeService.navigate('/app/sales-terminal/cart');
            this.storageService.set(RefundService.STORAGE_REFUND_EXCHANGE, false);
          } else {
            this.routeService.back();
          }
        }
      }
      this.giftTicketIds = refundCart.giftTicketIds;
      this.nonRefundableSurcharges = refundCart.nonRefundableSurcharges;
      if (refundCart.cart.total) {
        this.refundableInsurances = refundCart.cart.total.insurances.filter(insurance => insurance.refundable === true);
        this.nonRefundableInsurances = refundCart.cart.total.insurances.filter(insurance => insurance.refundable === false);
      }
      this.isLoading = false;
    });
  }

  private updateDeselectedTickets(ticketIds: number[], areTicketsRemoved: boolean): void {
    if (!ticketIds?.length) { return; }
    this.isLoading = true;
    this.deSelectedEvents = cloneDeep(this.refundEvents).map(event => {
      const deSelectedEvent: CartEvent = event;
      deSelectedEvent.legacyTickets = event.legacyTickets.filter(ticket => !this.selectedTicketIds.includes(ticket.id));
      return deSelectedEvent;
    });
    this.storageService.set(RefundService.STORAGE_REFUND_EVENTS_DESELECTED, JSON.stringify(this.deSelectedEvents));
    if (areTicketsRemoved) {
      this.removeTickets({tickets: ticketIds}).pipe(
        tap({error: () => this.isLoading = false})
      ).subscribe(_ => this.updateCart());
    } else {
      this.addTickets({tickets: ticketIds}).pipe(
        tap({error: () => this.isLoading = false})
      ).subscribe(_ => this.updateCart());
    }
  }

  getPaymentReversalsList(filters: PaymentReversalsFilter): Observable<PaymentReversalPage> {
    return this.api.getPaymentReversalsList(filters?.page, filters?.pageSize, filters?.reversalStartDate, filters?.reversalEndDate, filters?.refundStatus, filters?.eventId, filters?.orderConfirmation).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  getEventPaymentReversalsDetails(eventId: number): Observable<EventPaymentReversalsInfo> {
    return this.api.getEventPaymentReversalsInfo(eventId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public selfServiceRefund(id: number, refundTickets: RefundTickets): Observable<PurchaseTransaction> {
    return this.api.selfServiceTransactionIdRefundPost(id, refundTickets).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public selfServiceUpdatePayment(id: number, paymentMethod: PaymentMethodEnum): Observable<PurchaseTransaction> {
    return this.api.selfServiceTransactionIdRefundPut(id, '"' + paymentMethod + '"').pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public sendEventPaymentReversalsToEveryPay(eventId: number): Observable<any> {
    return this.api.sendEventPaymentReversalsToEveryPay(eventId).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }

  public sendPaymentReversalsToEveryPay(trxIds: number[]): Observable<any> {
    return this.api.sendPaymentReversalsToEveryPay(trxIds).pipe(
      tap({error: (e) => this.snackService.showBackendError(e)})
    );
  }
}
